import {
  Calendar,
  CalendarDots,
  MagnifyingGlass,
  UserCircle,
} from '@phosphor-icons/react';
import { Button, Input } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { BodyContainer, MuteText } from '../../elements/Common';
import { ClientHook } from '../../hooks';
import { AppTable } from '../../components/AppTable';
import { AnalyticCards, CompanyRow } from '../../page-components';
import { ListFilter } from '../../components';
import { CommonUtility } from '../../utility/common';
import { DateFormat, DateUtility } from '../../utility/date';
import { IconSizes } from '../../utility/constants/style-contants';
import { useAuth } from '../../contexts/auth';
import { useGlobalContext } from '../../contexts';

const ClientsToolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const MAX_DATA_LOOKBACK_DAYS = 365;

export function HomeScreen() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { warehouseId, warehouseLoading } = useGlobalContext();
  const {
    data,
    loading,
    exportCSV,
    exporting,
    updateAllFilter,
    filter: tableFilter,
    exportIndividualCSV,
  } = ClientHook({ warehouseId, warehouseLoading });
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const minStartDate = useMemo(
    () => moment().subtract(MAX_DATA_LOOKBACK_DAYS, 'days').startOf('day'),
    [],
  );

  const clampStartDate = useCallback(
    startDate => {
      if (!startDate) return startDate;

      const parsed = moment(startDate, DateFormat.yearMonthDate, true);
      if (!parsed.isValid()) return startDate;

      return parsed.isBefore(minStartDate)
        ? minStartDate.format(DateFormat.yearMonthDate)
        : startDate;
    },
    [minStartDate],
  );

  const setFilter = useCallback(
    next => {
      if (next.startDate) {
        next = { ...next, startDate: clampStartDate(next.startDate) };
      }
      updateAllFilter(next);
    },
    [updateAllFilter, clampStartDate],
  );

  const didHydrateFromUrl = useRef(false);

  useEffect(() => {
    if (didHydrateFromUrl.current) return;
    didHydrateFromUrl.current = true;

    const query = Object.fromEntries(searchParams.entries());
    if (query.startDate || query.endDate || query.timeRange) {
      updateAllFilter({
        startDate: clampStartDate(query.startDate || ''),
        endDate: query.endDate || '',
        timeRange: query.timeRange || '',
      });
    }
  }, [searchParams, updateAllFilter, clampStartDate]);

  useEffect(() => {
    const urlObject = {};
    Object.keys(tableFilter).forEach(key => {
      if (tableFilter[key]) {
        urlObject[key] = tableFilter[key];
      }
    });
    const nextSearch = new URLSearchParams(urlObject).toString();
    if (searchParams.toString() === nextSearch) return;

    navigate({ search: nextSearch }, { replace: true });
  }, [tableFilter, navigate, searchParams]);

  const filteredCompanies = useMemo(() => {
    if (!CommonUtility.isValidArray(data.activeCompanies)) return [];

    let companies = data.activeCompanies;

    companies = companies.sort(
      (company1, company2) => company2.mtdOrders - company1.mtdOrders,
    );

    if (!search) {
      return companies;
    }
    return companies.filter(
      x =>
        search.toLowerCase().includes(x.company_name?.toLowerCase()) ||
        x.company_name?.toLowerCase().includes(search?.toLowerCase()),
    );
  }, [data, search]);

  const currentOrgMetadata = useMemo(
    () => user?.organizations?.find(x => x._id === user?.organizationId)?.metadata || {},
    [user],
  );

  const columns = [
    {
      key: 'name',
      text: 'Company & Contact Person',
      textAlign: 'left',
    },
    {
      key: 'status',
      text: 'Status',
    },
    {
      key: 'orders',
      text: 'Total Orders',
    },
    {
      key: 'labels',
      text: 'Total Labels',
    },
    {
      key: 'label_purchases',
      text: 'Total Label Purchases',
    },
    {
      key: 'commission',
      text: 'Label Commission',
    },
  ];

  const filters = useMemo(() => {
    const query = Object.fromEntries(searchParams.entries());
    const temp = { ...query };
    Object.keys(temp).forEach(key => {
      const final = CommonUtility.tryParseJSONObject(temp[key]);
      if (final) {
        temp[key] = final;
      }
    });

    const defaultValues = { ...temp };

    return [
      {
        name: 'startDate',
        label: 'Start Date',
        icon: <Calendar />,
        type: 'date',
        dateFormat: DateFormat.yearMonthDate,
        defaultValue: defaultValues.startDate || null,
        disabledDate: current =>
          Boolean(current?.isBefore(minStartDate, 'day')) ||
          Boolean(current?.isAfter(moment(), 'day')),
      },
      {
        name: 'endDate',
        label: 'End Date',
        icon: <Calendar />,
        type: 'date',
        dateFormat: DateFormat.yearMonthDate,
        defaultValue: defaultValues.endDate || null,
        disabledDate: current =>
          Boolean(current?.isBefore(minStartDate, 'day')) ||
          Boolean(current?.isAfter(moment(), 'day')),
      },
      {
        name: 'timeRange',
        label: 'Time Range',
        icon: <CalendarDots />,
        options: [
          { label: 'Today', value: 'today' },
          { label: 'Yesterday', value: 'yesterday' },
          { label: '7 Days', value: '7days' },
          { label: '30 Days', value: '30days' },
          { label: '45 Days', value: '45days' },
          { label: '90 Days', value: '90days' },
        ],
        search: true,
        sorted: false,
        defaultValue: defaultValues.timeRange || null,
      },
    ];
  }, [searchParams, minStartDate]);

  return (
    <BodyContainer className="no-header">
      <div className="d-flex">
        <MuteText>Orders From</MuteText>
        <span className="mx-2">
          {DateUtility.formatDate(data?.start, DateFormat.month)}
        </span>
        <MuteText>To</MuteText>
        <span className="ml-2">
          {DateUtility.formatDate(data?.end, DateFormat.month)}
        </span>
      </div>
      <AnalyticCards
        currentOrgMetadata={currentOrgMetadata}
        loading={loading}
        data={data}
      />
      <div className="d-flex justify-content-between mt-3 flex-wrap align-items-center">
        <div className="d-flex align-items-center mr-4">
          <UserCircle size={27} />
          <h3 className="ml-2 mt-0 mb-0">Clients</h3>
        </div>
        <ClientsToolbar>
          <ListFilter filters={filters} filter={tableFilter} setFilter={setFilter} />
          <Button color="default" loading={exporting} onClick={exportCSV}>
            Export
          </Button>
          <Input
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            prefix={<MagnifyingGlass size={IconSizes.small} />}
            style={{ width: 220 }}
          />
        </ClientsToolbar>
      </div>
      <AppTable
        columns={columns}
        loading={loading}
        RowComponent={<CompanyRow exportData={exportIndividualCSV} />}
        data={filteredCompanies}
      />
    </BodyContainer>
  );
}
